import * as THREE from 'three';

import { gray2gradient, downloadCanvas, pointInConcavePolygon } from './geometry.js';
import { tic, toc } from './utils.js';
import { fillPolygon } from './dsm.js';
import { buildTextureLoadPlan, isLoadableTextureSource } from './textureLoadPlan.js';

function dataTextureToBase64Sync(dataTexture, type = 'image/jpeg', quality = 0.92) {
  try {
    const width = dataTexture.image.width;
    const height = dataTexture.image.height;
    const data = dataTexture.image.data;

    let canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    let ctx = canvas.getContext('2d');

    const isOneChannelsTexture = data.length === width * height;
    const isFloatTexture = data instanceof Float32Array;

    const imgData = ctx.createImageData(width, height);
    if (isOneChannelsTexture) {
      for (let i = 0; i < imgData.data.length; i += 4) {
        imgData.data[i] = isFloatTexture ? Math.round(data[i / 4] * 255) : data[i / 4];
        imgData.data[i + 1] = imgData.data[i];
        imgData.data[i + 2] = imgData.data[i];
        imgData.data[i + 3] = 255; // Alpha channel
      }
    } else {
      for (let i = 0; i < imgData.data.length; i += 1) {
        imgData.data[i] = isFloatTexture ? Math.round(data[i] * 255) : data[i];
      }
    }

    ctx.putImageData(imgData, 0, 0);

    const base64Image = canvas.toDataURL(type, quality);

    canvas.width = 1;
    canvas.height = 1;
    ctx = null;
    canvas.remove();
    canvas = null;

    return base64Image;
  } catch (error) {
    console.error('An error occurred:', error);
    return null;
  }
}

function requireBase64Image(base64Image, label) {
  if (typeof base64Image === 'string' && base64Image.startsWith('data:image/')) {
    return base64Image;
  }
  throw new Error(`Failed to serialize shading texture: ${label}`);
}

const serializeShadingTexture = (texture, type = 'image/jpeg') =>
  requireBase64Image(dataTextureToBase64Sync(texture, type), 'calculated shading');

export default class Materials {
  canvasPool;

  TSRFTextures = {};

  TOFTextures = {};

  SolarAccessTextures = {};

  diffuseTextures = {};

  activeShadingTextureJobs = new Set();

  shadingTextureWaiters = {};

  constructor(canvasPool) {
    this.canvasPool = canvasPool;
  }

  hasShadingTextures = (month = 0) =>
    !!(this.SolarAccessTextures[month]?.[0] && this.TOFTextures[month]?.[0] && this.TSRFTextures[month]?.[0]);

  trackShadingTextureJob = (job) => {
    const trackedJob = Promise.resolve(job).finally(() => {
      this.activeShadingTextureJobs.delete(trackedJob);
    });
    this.activeShadingTextureJobs.add(trackedJob);
    return trackedJob;
  };

  beginShadingTextureJob = () => {
    let finishJob;
    const job = new Promise((resolve) => {
      finishJob = resolve;
    });
    this.trackShadingTextureJob(job);
    return finishJob;
  };

  getShadingTextureWaiter = (month = 0) => {
    if (this.hasShadingTextures(month)) return null;
    if (!this.shadingTextureWaiters[month]) {
      let resolve;
      const promise = new Promise((resolvePromise) => {
        resolve = resolvePromise;
      });
      this.shadingTextureWaiters[month] = { promise, resolve };
    }
    return this.shadingTextureWaiters[month];
  };

  notifyShadingTexturesChanged = (month = 0) => {
    if (!this.hasShadingTextures(month)) return;
    const waiter = this.shadingTextureWaiters[month];
    if (!waiter) return;
    waiter.resolve(true);
    delete this.shadingTextureWaiters[month];
  };

  waitForShadingTextures = async (month = 0) => {
    if (this.hasShadingTextures(month)) return true;
    if (this.activeShadingTextureJobs.size === 0) return false;

    const waiter = this.getShadingTextureWaiter(month);
    const activeJobs = Promise.allSettled([...this.activeShadingTextureJobs]);

    await Promise.race([waiter.promise, activeJobs]);

    if (!this.hasShadingTextures(month)) {
      if (this.shadingTextureWaiters[month] === waiter) {
        delete this.shadingTextureWaiters[month];
      }
      return false;
    }

    return true;
  };

  getMaskCanvas = (segments, width = null, height = null) => {
    const newCanvas = this.canvasPool.getCanvas(this.canvasPool.PoolKeys.GetMaskCanvas, width, height);
    const context = this.canvasPool.getContext(this.canvasPool.PoolKeys.GetMaskCanvas, width, height);
    context.clearRect(0, 0, newCanvas.width, newCanvas.height);
    segments.forEach((seg) => {
      fillPolygon(context, seg.geometry, `rgb(${255}, ${255}, ${255})`);
    });
    return newCanvas;
  };

  isFloatTexture = (texture) => {
    return texture && texture.type && texture.type === THREE.FloatType;
  };

  saveTextures = (info, shadingData = null, diffuseData = null, dimensions = null) => {
    if (shadingData) {
      const { width, height } = dimensions || {};
      if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0 ||
        !Array.isArray(diffuseData) || diffuseData.length !== 4) {
        throw new Error('Unable to save incomplete shading textures');
      }
      const serialize = (data, type = 'image/jpeg') => {
        if (!data || ![width * height, width * height * 4].includes(data.length) || !data.every(Number.isFinite)) {
          throw new Error('Unable to save invalid shading samples');
        }
        return serializeShadingTexture({ image: { width, height, data } }, type);
      };
      const periods = Array.from({ length: 13 }, (_, month) => {
        const values = shadingData[month];
        if (!Array.isArray(values) || values.length !== 3) throw new Error('Unable to save incomplete shading periods');
        return values;
      });
      return {
        ...info,
        diffuse: diffuseData.map((data) => serialize(data)),
        tsrf: periods.map((values) => serialize(values[0])),
        tof: periods.map((values) => serialize(values[1])),
        solarAccess: periods.map((values) => serialize(values[2], 'image/png'))
      };
    }
    const periods = Array.from({ length: 13 }, (_, month) => month);
    if (![this.TSRFTextures, this.TOFTextures, this.SolarAccessTextures].every((collection) =>
      periods.every((month) => collection[month]?.[0]))) {
      throw new Error('Unable to save incomplete shading periods');
    }
    const data = {
      diffuse: [],
      tsrf: [],
      tof: [],
      solarAccess: [],
      ...info
    };
    const {
      tsrf_iso: diffuseTSRF_iso,
      tof_iso: diffuseTOF_iso,
      tsrf_horizon: diffuseTSRF_horizon,
      tof_horizon: diffuseTOF_horizon
    } = this.diffuseTextures;
    const diffuseTSRFData = requireBase64Image(dataTextureToBase64Sync(diffuseTSRF_iso), 'diffuse_tsrf_iso');
    const diffuseTOFData = requireBase64Image(dataTextureToBase64Sync(diffuseTOF_iso), 'diffuse_tof_iso');
    const diffuseTSRFHorizonData = requireBase64Image(dataTextureToBase64Sync(diffuseTSRF_horizon), 'diffuse_tsrf_horizon');
    const diffuseTOFHorizonData = requireBase64Image(dataTextureToBase64Sync(diffuseTOF_horizon), 'diffuse_tof_horizon');
    data.diffuse.push(diffuseTSRFData);
    data.diffuse.push(diffuseTOFData);
    data.diffuse.push(diffuseTSRFHorizonData);
    data.diffuse.push(diffuseTOFHorizonData);
    Object.keys(this.TSRFTextures).forEach((i) => {
      const tsrfGray = this.TSRFTextures[i][0];
      const tofGray = this.TOFTextures[i][0];
      const saGray = this.SolarAccessTextures[i][0];
      const tsrfData = requireBase64Image(dataTextureToBase64Sync(tsrfGray), `tsrf_${i}`);
      const tofData = requireBase64Image(dataTextureToBase64Sync(tofGray), `tof_${i}`);
      const saData = requireBase64Image(dataTextureToBase64Sync(saGray, 'image/png'), `solarAccess_${i}`);
      data.tsrf.push(tsrfData);
      data.tof.push(tofData);
      data.solarAccess.push(saData);
    });
    return data;
  };

  getPanelRectPoints = (point, width, height, azimuth, renderedRect, scale, center) => {
    let points;
    const hasRenderedRect =
      Array.isArray(renderedRect) &&
      renderedRect.length === 4 &&
      renderedRect.every((point) => Number.isFinite(point?.x) && Number.isFinite(point?.y));
    const hasRenderedTransform =
      Number.isFinite(scale) &&
      scale !== 0 &&
      Array.isArray(center) &&
      Number.isFinite(center[0]) &&
      Number.isFinite(center[1]);
    if (hasRenderedRect && hasRenderedTransform) {
      points = renderedRect.map(({ x, y }) => new THREE.Vector3(x / scale + center[0], y / scale + center[1], 0));
    } else {
      const [x, y] = point;
      const az = (azimuth * Math.PI) / 180.0;
      points = [
        [x, y],
        [x, y + height],
        [x + width, y + height],
        [x + width, y]
      ].map(([vx, vy]) => {
        const newX = x + (vx - x) * Math.cos(az) - (vy - y) * Math.sin(az);
        const newY = y + (vx - x) * Math.sin(az) + (vy - y) * Math.cos(az);
        return new THREE.Vector3(newX, newY, 0);
      });
    }
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const minx = Math.round(Math.min(...xs));
    const maxx = Math.round(Math.max(...xs));
    const miny = Math.round(Math.min(...ys));
    const maxy = Math.round(Math.max(...ys));
    const ranges = [minx, maxx, miny, maxy];
    return [points, ranges];
  };

  downloadPanelShadingOverlay = (texture, panelRects, fileName) => {
    if (typeof document === 'undefined' || !texture?.image?.data || !texture.image.width || !texture.image.height) {
      return;
    }

    const { data, width, height } = texture.image;
    const pixelCount = width * height;
    const channels = Math.round(data.length / pixelCount);
    const isByte = data instanceof Uint8Array || data instanceof Uint8ClampedArray;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    const imageData = context.createImageData(width, height);

    for (let pixel = 0; pixel < pixelCount; pixel += 1) {
      const sourceIndex = pixel * channels;
      const targetIndex = pixel * 4;
      if (channels === 1) {
        const value = Number(data[sourceIndex]);
        const normalized = Math.max(0, Math.min(1, isByte ? value / 255 : value));
        const [r, g, b] = gray2gradient(normalized);
        imageData.data[targetIndex] = r;
        imageData.data[targetIndex + 1] = g;
        imageData.data[targetIndex + 2] = b;
        imageData.data[targetIndex + 3] = 255;
      } else {
        const scale = isByte ? 1 : 255;
        imageData.data[targetIndex] = Number(data[sourceIndex]) * scale;
        imageData.data[targetIndex + 1] = Number(data[sourceIndex + 1] ?? data[sourceIndex]) * scale;
        imageData.data[targetIndex + 2] = Number(data[sourceIndex + 2] ?? data[sourceIndex]) * scale;
        imageData.data[targetIndex + 3] = channels === 4 ? Number(data[sourceIndex + 3]) * scale : 255;
      }
    }

    context.putImageData(imageData, 0, 0);
    context.lineWidth = 2;
    context.font = 'bold 12px sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    panelRects.forEach(({ segmentIndex, panelIndex, orientation, rect, solarAccess }) => {
      if (!Array.isArray(rect) || rect.length < 3) return;
      context.beginPath();
      context.moveTo(rect[0].x, rect[0].y);
      rect.slice(1).forEach((point) => context.lineTo(point.x, point.y));
      context.closePath();
      context.fillStyle = 'rgba(0, 255, 255, 0.18)';
      context.fill();
      context.strokeStyle = '#00ffff';
      context.stroke();

      const center = rect.reduce(
        (result, point) => ({
          x: result.x + point.x / rect.length,
          y: result.y + point.y / rect.length
        }),
        { x: 0, y: 0 }
      );
      const label = `${segmentIndex}-${panelIndex} ${orientation[0].toUpperCase()} ${(solarAccess * 100).toFixed(1)}%`;
      context.lineWidth = 3;
      context.strokeStyle = '#000000';
      context.strokeText(label, center.x, center.y);
      context.fillStyle = '#ffffff';
      context.fillText(label, center.x, center.y);
      context.lineWidth = 2;
    });

    downloadCanvas(canvas, fileName);
    canvas.width = 1;
    canvas.height = 1;
    canvas.remove();
  };

  getAnualPanelsShadings = async (segments, panels, _gwidth, _gheight, options = {}) => {
    console.log('getAnualPanelsShadings: Starting shading calculations (with coverage score)...');
    const textureOptions =
      options && typeof options === 'object'
        ? options
        : _gheight && typeof _gheight === 'object'
          ? _gheight
          : _gwidth && typeof _gwidth === 'object'
            ? _gwidth
            : {};
    const waitForTextures = textureOptions.waitForTextures !== false;

    // Track total polygons processed vs. how many have at least 1 in-bounds pixel
    let totalPolygons = 0;
    let coveredPolygons = 0;

    try {
      // 1) Wait for baseline (month=0) textures to load or bail out
      if (waitForTextures && !this.hasShadingTextures(0)) {
        console.warn('[getAnualPanelsShadings] Waiting for month=0 textures...', {
          solarAccess0: !!this.SolarAccessTextures[0]?.[0],
          tof0: !!this.TOFTextures[0]?.[0],
          tsrf0: !!this.TSRFTextures[0]?.[0],
          activeTextureJobs: this.activeShadingTextureJobs.size
        });
        await this.waitForShadingTextures(0);
      }

      if (!this.hasShadingTextures(0)) {
        if (waitForTextures) {
          console.error('[getAnualPanelsShadings] Missing baseline textures (month=0). Returning [].');
        } else {
          console.warn(
            '[getAnualPanelsShadings] Missing baseline textures (month=0). Skipping texture-derived shading without waiting.'
          );
        }
        return [];
      }

      const grayTexture = this.SolarAccessTextures[0][0];
      const grayTOFTexture = this.TOFTextures[0][0];
      const grayTSRFTexture = this.TSRFTextures[0][0];

      if (!grayTexture.image || !grayTOFTexture.image || !grayTSRFTexture.image) {
        console.error('[getAnualPanelsShadings] Baseline textures loaded, but .image is null. Returning [].');
        return [];
      }

      const makeSampler = (img) => {
        const w = img?.width;
        const h = img?.height;
        const d = img?.data;
        const denom = w && h ? w * h : 0;
        let channels = denom > 0 && d?.length ? Math.round(d.length / denom) : 4;
        if (![1, 2, 3, 4].includes(channels)) channels = 4;
        const isByte = d instanceof Uint8Array || d instanceof Uint8ClampedArray;
        const scale = isByte ? 255.0 : 1.0;
        return (x, y) => {
          if (!d || !w || !h) return 0;
          if (x < 0 || y < 0 || x >= w || y >= h) return 0;
          const base = (y * w + x) * channels;
          if (base < 0 || base >= d.length) return 0;
          const n = Number(d[base]);
          return Number.isFinite(n) ? n / scale : 0;
        };
      };

      const sampleSA0 = makeSampler(grayTexture.image);
      const sampleTOF0 = makeSampler(grayTOFTexture.image);
      const sampleTSRF0 = makeSampler(grayTSRFTexture.image);
      let isRoofPixel = () => true;
      try {
        const maskKey = this.canvasPool?.PoolKeys?.GetMaskCanvas;
        const roofMaskContext =
          maskKey && this.canvasPool?.getContext
            ? this.canvasPool.getContext(maskKey)
            : null;
        if (roofMaskContext?.getImageData) {
          const roofMaskImage = roofMaskContext.getImageData(
            0,
            0,
            grayTexture.image.width,
            grayTexture.image.height
          );
          let hasRoof = false;
          for (let i = 0; i < roofMaskImage.data.length; i += 4) {
            if (roofMaskImage.data[i] > 0) {
              hasRoof = true;
              break;
            }
          }
          if (hasRoof) {
            isRoofPixel = (x, y) => {
              if (x < 0 || y < 0 || x >= roofMaskImage.width || y >= roofMaskImage.height) {
                return false;
              }
              return roofMaskImage.data[(y * roofMaskImage.width + x) * 4] > 0;
            };
          }
        }
      } catch (err) {
        console.warn('Failed to extract roof mask', err);
      }

      // Main shading data
      const values = [];
      const panelRects = [];

      // ========== BASELINE PASS (month=0) ==========
      for (let i = 0; i < panels.length; i += 1) {
        let panelIndex = 0;
        for (let k = 0; k < 2; k += 1) {
          const t = k === 0 ? 'horizontal' : 'vertical';
          const panelGroup = panels[i][t];
          if (!panelGroup || !panelGroup.points) continue;

          for (let j = 0; j < panelGroup.points.length; j += 1) {
            // Count polygons
            totalPolygons++;

            const [rect, [minx, maxx, miny, maxy]] = this.getPanelRectPoints(
              panelGroup.points[j],
              panelGroup.width,
              panelGroup.height,
              segments[i].panelOrientation,
              textureOptions.panelData?.[i]?.points3D?.[panelIndex],
              textureOptions.panelScale,
              textureOptions.panelCenter
            );

            let n = 0;
            let sumSA = 0;
            let sumTOF = 0;
            let sumTSRF = 0;

            for (let x = minx; x <= maxx; x++) {
              for (let y = miny; y <= maxy; y++) {
                const p = new THREE.Vector3(x, y, 0);
                if (pointInConcavePolygon(rect, p) && isRoofPixel(x, y)) {
                  n++;
                  const v = sampleSA0(x, y);
                  const tofV = sampleTOF0(x, y);
                  const tsrfV = sampleTSRF0(x, y);
                  sumSA += v;
                  sumTOF += tofV;
                  sumTSRF += tsrfV;
                }
              }
            }

            if (n > 0) {
              coveredPolygons++;
            }

            const [v1, v2, v3, v4] = rect;
            const vrect = [
              v1,
              v1.clone().add(v2).multiplyScalar(0.5),
              v2,
              v2.clone().add(v3).multiplyScalar(0.5),
              v3,
              v3.clone().add(v4).multiplyScalar(0.5),
              v4,
              v4.clone().add(v1).multiplyScalar(0.5)
            ];

            const solarAccess = n > 0 ? sumSA / n : 0;
            values.push([
              i,
              t,
              panelIndex,
              (minx + maxx) / 2,
              (miny + maxy) / 2,
              vrect,
              solarAccess,
              n > 0 ? sumTOF / n : 0,
              n > 0 ? sumTSRF / n : 0
            ]);
            panelRects.push({
              segmentIndex: i,
              panelIndex,
              orientation: t,
              rect,
              solarAccess
            });
            panelIndex += 1;
          }
        }
      }

      // ========== MONTHLY PASS (1..12) ==========
      const monthlyValues = {};

      for (let m = 1; m <= 12; m++) {
        // Check if monthly textures exist
        if (
          !this.SolarAccessTextures[m] ||
          !this.SolarAccessTextures[m][0] ||
          !this.TOFTextures[m] ||
          !this.TOFTextures[m][0] ||
          !this.TSRFTextures[m] ||
          !this.TSRFTextures[m][0]
        ) {
          console.warn(`[getAnualPanelsShadings] Month=${m} missing textures, skipping.`);
          monthlyValues[m] = [];
          continue;
        }

        const grayTextureMonth = this.SolarAccessTextures[m][0];
        const grayTOFTextureMonth = this.TOFTextures[m][0];
        const grayTSRFTextureMonth = this.TSRFTextures[m][0];

        if (!grayTextureMonth.image || !grayTOFTextureMonth.image || !grayTSRFTextureMonth.image) {
          console.warn(`[getAnualPanelsShadings] Month=${m} texture .image is null, skipping.`);
          monthlyValues[m] = [];
          continue;
        }

        const sampleSAm = makeSampler(grayTextureMonth.image);
        const sampleTOFm = makeSampler(grayTOFTextureMonth.image);
        const sampleTSRFm = makeSampler(grayTSRFTextureMonth.image);

        const monthArray = [];

        for (let i = 0; i < panels.length; i += 1) {
          let panelIndex = 0;
          for (let k = 0; k < 2; k += 1) {
            const t = k === 0 ? 'horizontal' : 'vertical';
            const panelGroup = panels[i][t];
            if (!panelGroup || !panelGroup.points) continue;

            for (let j = 0; j < panelGroup.points.length; j += 1) {
              const [rect, [minx, maxx, miny, maxy]] = this.getPanelRectPoints(
                panelGroup.points[j],
                panelGroup.width,
                panelGroup.height,
                segments[i].panelOrientation,
                textureOptions.panelData?.[i]?.points3D?.[panelIndex],
                textureOptions.panelScale,
                textureOptions.panelCenter
              );

              let n = 0;
              let sumSA = 0;
              let sumTOF = 0;
              let sumTSRF = 0;

              for (let x = minx; x <= maxx; x++) {
                for (let y = miny; y <= maxy; y++) {
                  const p = new THREE.Vector3(x, y, 0);
                  if (pointInConcavePolygon(rect, p) && isRoofPixel(x, y)) {
                    n++;
                    sumSA += sampleSAm(x, y);
                    sumTOF += sampleTOFm(x, y);
                    sumTSRF += sampleTSRFm(x, y);
                  }
                }
              }

              const [v1, v2, v3, v4] = rect;
              const vrect = [
                v1,
                v1.clone().add(v2).multiplyScalar(0.5),
                v2,
                v2.clone().add(v3).multiplyScalar(0.5),
                v3,
                v3.clone().add(v4).multiplyScalar(0.5),
                v4,
                v4.clone().add(v1).multiplyScalar(0.5)
              ];

              monthArray.push([
                i,
                t,
                panelIndex,
                (minx + maxx) / 2,
                (miny + maxy) / 2,
                vrect,
                n > 0 ? sumSA / n : 0,
                n > 0 ? sumTOF / n : 0,
                n > 0 ? sumTSRF / n : 0
              ]);
              panelIndex++;
            }
          }
        }

        monthlyValues[m] = monthArray;
      }

      // Append monthly data to the main array
      values.push(monthlyValues);

      if (textureOptions.downloadPanelOverlay) {
        const overlayTexture = this.SolarAccessTextures[0][1] || grayTexture;
        this.downloadPanelShadingOverlay(
          overlayTexture,
          panelRects,
          textureOptions.panelOverlayFileName || `proposal-solar-access-panels-${Date.now()}`
        );
      }

      // ========== LOG COVERAGE SCORE ==========
      const coverageScore = totalPolygons > 0 ? coveredPolygons / totalPolygons : 1.0;
      const coveragePct = (coverageScore * 100).toFixed(2);

      // Always log coverage, even if 100%
      console.log(
        `[getAnualPanelsShadings] Done. Coverage Score: ${coveragePct}% (${coveredPolygons}/${totalPolygons} polygons had in-bounds pixels).`
      );

      return values;
    } catch (err) {
      console.error('[getAnualPanelsShadings] Unexpected error:', err);
      console.warn('Returning empty array to avoid crashing further logic.');
      return [];
    }
  };

  gradiantData = (grayData, width, height, ranges, floatType) => {
    if (!ranges) return grayData;

    tic();

    const [minx, miny, maxx, maxy] = ranges;

    const maskContext = this.canvasPool.getContext(this.canvasPool.PoolKeys.GetMaskCanvas);
    const maskData = maskContext.getImageData(0, 0, width, height);

    const srcData = grayData;
    const data_scale = floatType ? 1 : 255;

    try {
      const destData = new Uint8Array(width * height * 4);

      for (let x = minx; x < maxx; x += 1) {
        for (let y = miny; y < maxy; y += 1) {
          const maskIndex = y * (width * 4) + x * 4;
          const maskValue = maskData.data[maskIndex];
          if (maskValue !== 0) {
            const index = floatType ? y * width + x : maskIndex;
            const v = srcData[index];
            const vNorm = Math.max(0, Math.min(1, Number(v) / data_scale));
            const gradiant = gray2gradient(vNorm);
            [destData[maskIndex], destData[maskIndex + 1], destData[maskIndex + 2]] = gradiant;
            destData[maskIndex + 3] = 255;
          }
        }
      }

      return destData;
    } catch (e) {
      return grayData;
    }
  };

  gradiantDataTexture = (grayTexture, ranges, mask, name) => {
    if (!ranges) return grayTexture;

    tic();

    const { width, height } = grayTexture.image;
    const [minx, miny, maxx, maxy] = ranges;

    const maskContext = this.canvasPool.getContext(this.canvasPool.PoolKeys.GetMaskCanvas);
    const maskData = maskContext.getImageData(0, 0, width, height);
    const srcContext = this.canvasPool.getContext(name);
    const srcData = srcContext.getImageData(0, 0, width, height);

    try {
      const destData = new Uint8Array(width * height * 4);

      for (let x = minx; x < maxx; x += 1) {
        for (let y = miny; y < maxy; y += 1) {
          const index = y * (width * 4) + x * 4;
          const maskValue = maskData.data[index];
          if (maskValue !== 0) {
            const v = Math.max(0, Math.min(255, srcData.data[index]));
            const gradiant = gray2gradient(v / 255);
            [destData[index], destData[index + 1], destData[index + 2]] = gradiant;
            destData[index + 3] = 255;
          }
        }
      }

      const texture = new THREE.DataTexture(destData, width, height, THREE.RGBAFormat, THREE.UnsignedByteType);
      texture.flipY = true;
      texture.needsUpdate = true;

      return texture;
    } catch (e) {
      return grayTexture;
    }
  };

  loadBase64ToTexture = (name, month, base64) =>
    new Promise((resolve, reject) => {
      if (!isLoadableTextureSource(base64)) {
        reject(new TypeError(`Cannot load empty texture source for ${name}`));
        return;
      }

      const image = new Image();
      image.onload = () => {
        const width = image.naturalWidth || image.width || 1024;
        const height = image.naturalHeight || image.height || 1024;
        let canvas = null;
        try {
          canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(image, 0, 0, width, height);
          const pixelData = ctx.getImageData(0, 0, width, height).data;
          const texture = this.dataToTexture(pixelData, width, height);
          resolve([name, month, texture, pixelData, width, height]);
        } catch (error) {
          reject(error);
        } finally {
          if (canvas) {
            canvas.width = 1;
            canvas.height = 1;
            canvas.remove();
          }
        }
      };
      image.onerror = reject;
      image.src = base64;
    });

  dataToTexture = (data, width, height) => {
    const shadingTexture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat, THREE.UnsignedByteType);
    shadingTexture.flipY = true;
    // shadingTexture.flipX = true;
    shadingTexture.needsUpdate = true;
    return shadingTexture;
  };

  getCollectionByName = (name) => {
    if (name.startsWith('diffuse_')) return this.diffuseTextures;
    if (name.startsWith('tsrf_')) return this.TSRFTextures;
    if (name.startsWith('tof_')) return this.TOFTextures;
    if (name.startsWith('solarAccess_')) return this.SolarAccessTextures;
    return null;
  };

  disposeTextureValue = (value) => {
    if (Array.isArray(value)) {
      value.forEach((entry) => this.disposeTextureValue(entry));
    } else if (value?.dispose) {
      value.dispose();
    }
  };

  disposeTextureCollection = (collection) => {
    Object.values(collection || {}).forEach((value) => this.disposeTextureValue(value));
  };

  resetTSRFTextures = () => {
    for (let i = 0; i <= 12; i += 1) this.clean(i, 'TSRF');
  };

  setTSRFTextures = (m, grayTSRF, gradientTSRF) => {
    this.clean(m, 'TSRF');
    this.TSRFTextures[m] = [grayTSRF, gradientTSRF];
    this.notifyShadingTexturesChanged(m);
  };

  resetTOFTextures = () => {
    for (let i = 0; i <= 12; i += 1) this.clean(i, 'TOF');
  };

  setTOFTextures = (m, grayTOF, gradientTOF) => {
    this.clean(m, 'TOF');
    this.TOFTextures[m] = [grayTOF, gradientTOF];
    this.notifyShadingTexturesChanged(m);
  };

  resetSolarAccessTextures = () => {
    for (let i = 0; i <= 12; i += 1) this.clean(i, 'SA');
  };

  setSolarAccessTextures = (m, graySolarAccess, gradientSolarAccess) => {
    this.clean(m, 'SA');
    this.SolarAccessTextures[m] = [graySolarAccess, gradientSolarAccess];
    this.notifyShadingTexturesChanged(m);
  };

  loadTextures = async (data) => {
    const loadJob = (async () => {
      const shadingRoot = data?.shading_data || data;
      const { diffuse, tsrf, tof, solarAccess } = shadingRoot || {};
      const { ranges = null, segments = [] } = data || {};

      if (
        !Array.isArray(diffuse) ||
        ![2, 4].includes(diffuse.length) ||
        !Array.isArray(tsrf) ||
        !Array.isArray(tof) ||
        !Array.isArray(solarAccess)
      ) {
        throw new Error('Incomplete shading textures');
      }
      if (![tsrf, tof, solarAccess].every((values) => values.length > 0) ||
        ![diffuse, tsrf, tof, solarAccess].every((values) => Array.from(values).every(isLoadableTextureSource))) {
        throw new Error('Incomplete shading textures');
      }
      if (tsrf.length !== tof.length || tsrf.length !== solarAccess.length) {
        throw new Error('Mismatched shading payload lengths');
      }

      const tasks = buildTextureLoadPlan(shadingRoot).map(({ name, month, source }) =>
        this.loadBase64ToTexture(name, month, source)
      );
      const result = await Promise.allSettled(tasks);
      const rejected = result.filter((item) => item.status === 'rejected');
      if (rejected.length > 0) {
        result.forEach((item) => {
          if (item.status === 'fulfilled') {
            this.disposeTextureValue(item.value[2]);
          }
        });
        throw new Error(`Failed to decode ${rejected.length} shading texture(s)`);
      }
      const first = result.find((item) => item.status === 'fulfilled');
      const [, , , , shadingWidth = 1024, shadingHeight = 1024] = first?.value || [];
      const nextDiffuseTextures = {};
      const nextTSRFTextures = {};
      const nextTOFTextures = {};
      const nextSolarAccessTextures = {};
      const nextCollections = {
        diffuse: nextDiffuseTextures,
        tsrf: nextTSRFTextures,
        tof: nextTOFTextures,
        solarAccess: nextSolarAccessTextures
      };
      const createdTextures = new Set(result.map((item) => item.value[2]));

      let maskContext;
      let previousMask;
      try {
        if (segments?.length) {
          const maskCanvas = this.canvasPool.getCanvas(this.canvasPool.PoolKeys.GetMaskCanvas);
          maskContext = this.canvasPool.getContext(this.canvasPool.PoolKeys.GetMaskCanvas);
          previousMask = maskContext.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
          this.getMaskCanvas(segments, shadingWidth, shadingHeight);
        }
        result.forEach((item) => {
          if (item.status === 'fulfilled') {
            const [name, m, texture, pixelData, width, height] = item.value;
            const prefix = name.split('_')[0];
            const collection = nextCollections[prefix];
            if (collection === nextDiffuseTextures) {
              collection[m] = texture;
            } else {
              const gradientData = this.gradiantData(pixelData, width, height, ranges);
              const gradientTexture = this.dataToTexture(gradientData, width, height);
              createdTextures.add(gradientTexture);
              collection[m] = [texture, gradientTexture];
            }
          }
        });
      } catch (error) {
        createdTextures.forEach((texture) => this.disposeTextureValue(texture));
        if (previousMask) maskContext.putImageData(previousMask, 0, 0);
        throw error;
      }

      const previousCollections = [this.diffuseTextures, this.TSRFTextures, this.TOFTextures, this.SolarAccessTextures];
      this.diffuseTextures = nextDiffuseTextures;
      this.TSRFTextures = nextTSRFTextures;
      this.TOFTextures = nextTOFTextures;
      this.SolarAccessTextures = nextSolarAccessTextures;
      previousCollections.forEach((collection) => this.disposeTextureCollection(collection));
      Object.keys(nextSolarAccessTextures).forEach((month) => this.notifyShadingTexturesChanged(month));
    })();

    return this.trackShadingTextureJob(loadJob);
  };

  clean = (m, type) => {
    let collection = null;

    if (type === 'TSRF') {
      collection = this.TSRFTextures;
    } else if (type === 'TOF') {
      collection = this.TOFTextures;
    } else if (type === 'SA') {
      collection = this.SolarAccessTextures;
    }

    if (collection && m in collection) {
      this.disposeTextureValue(collection[m]);
      delete collection[m];
    }
  };
}
